import React from 'react';
import LoadingSpinner from './LoadingSpinner.jsx';
import { PREFERENCES_STATUS, useNotificationPreferences } from '../../hooks/useNotificationPreferences.js';
import { PUSH_UNAVAILABLE_KEYS } from '../../_helpers/notifications.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import { englishT } from '../../locales/index.js';
import styles from '../../styles/NotificationsPage.module.css';

/* English copy of the panel's fixed messages, for tests and callers without a locale. */
export const UNAVAILABLE_TEXT = englishT('notifications.preferences.unavailable');
export const CHANNEL_UNAVAILABLE_TEXT = englishT('notifications.preferences.channelUnavailable');

/** Why `entry` cannot be switched, in the active locale; null when it can. */
const unavailableText = (entry, t) => {
  if (entry.available) return null;
  const key = PUSH_UNAVAILABLE_KEYS[entry.unavailableReason] ?? 'notifications.preferences.channelUnavailable';
  return t(key);
};

/**
 * A native-button switch: focusable, toggles on Enter/Space, announces as a
 * switch with its checked state. Disabled (not hidden) when the option is
 * unavailable so the reason next to it is reachable.
 */
function PreferenceSwitch({ id, label, checked, disabled, note, onChange }) {
  const noteId = note ? `${id}-note` : undefined;
  return (
    <li className={styles.prefRow}>
      <div className={styles.prefText}>
        <span id={`${id}-label`} className={styles.prefLabel}>{label}</span>
        {note && (
          <span id={noteId} className={styles.prefNote}>{note}</span>
        )}
      </div>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={noteId}
        disabled={disabled}
        className={`${styles.switch}${checked ? ` ${styles.switchOn}` : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className={styles.switchKnob} aria-hidden="true" />
      </button>
    </li>
  );
}

/**
 * Notification settings for the signed-in listener (ENG-26). Renders the
 * backend's matrix and nothing else: a group per event it lists, a switch per
 * channel that event can be delivered on (ENG-409). There is no global
 * channel switch and no per-event mute, because the backend stores no such
 * thing — only the cells. A channel the backend or the browser cannot deliver
 * is shown disabled with the reason, at whatever state the backend holds for
 * it: every save PUTs the whole set, so the switch must show the state that
 * would be sent, never a local default in its place.
 */
function NotificationPreferences({ enabled = true, pushOptions }) {
  const {
    preferences,
    status,
    error,
    saving,
    saveError,
    saved,
    setChannelEnabled,
    reload,
    dismissSaveError,
  } = useNotificationPreferences({ enabled, pushOptions });
  const t = useT();

  if (status === PREFERENCES_STATUS.IDLE || status === PREFERENCES_STATUS.UNAUTHORIZED) return null;

  const renderBody = () => {
    if (status === PREFERENCES_STATUS.LOADING) {
      return (
        <div className={styles.prefState} data-testid="preferences-loading">
          <LoadingSpinner />
        </div>
      );
    }
    if (status === PREFERENCES_STATUS.UNAVAILABLE) {
      return <p className={styles.mutedText} data-testid="preferences-unavailable">{t('notifications.preferences.unavailable')}</p>;
    }
    if (status === PREFERENCES_STATUS.ERROR) {
      return (
        <div className={styles.prefState}>
          <p className={styles.errorText} role="alert">{error}</p>
          <button type="button" className={styles.secondaryButton} onClick={reload}>{t('common.actions.tryAgain')}</button>
        </div>
      );
    }
    const events = preferences?.events ?? [];
    if (events.length === 0) {
      return <p className={styles.mutedText}>{t('notifications.preferences.none')}</p>;
    }
    return events.map((event) => (
      <fieldset key={event.id} className={styles.prefGroup} disabled={saving}>
        <legend className={styles.prefLegend}>{event.labelKey ? t(event.labelKey) : event.label}</legend>
        <ul className={styles.prefList}>
          {event.channels.map((channel) => (
            <PreferenceSwitch
              key={channel.id}
              id={`${event.id}-${channel.id}`}
              label={channel.labelKey ? t(channel.labelKey) : channel.label}
              checked={channel.enabled}
              disabled={!channel.available || saving}
              note={unavailableText(channel, t)}
              onChange={(value) => setChannelEnabled(event.id, channel.id, value)}
            />
          ))}
        </ul>
      </fieldset>
    ));
  };

  return (
    <section className={styles.preferences} aria-labelledby="notification-preferences-title" data-testid="notification-preferences">
      <div className={styles.sectionHeader}>
        <h2 id="notification-preferences-title" className={styles.sectionTitle}>{t('notifications.preferences.title')}</h2>
        <span className={styles.savedNotice} role="status" aria-live="polite" data-testid="preferences-status">
          {saving ? t('common.actions.savingEllipsis') : saved ? t('notifications.preferences.saved') : ''}
        </span>
      </div>
      {saveError && (
        <div className={styles.inlineError} role="alert">
          <span>{saveError.message}</span>
          <div className={styles.inlineActions}>
            <button type="button" className={styles.secondaryButton} onClick={saveError.retry}>{t('common.actions.retry')}</button>
            <button type="button" className={styles.textButton} onClick={dismissSaveError}>{t('common.actions.dismiss')}</button>
          </div>
        </div>
      )}
      {renderBody()}
    </section>
  );
}

export default NotificationPreferences;
